import { useEffect, useState } from "react";

import { Menu } from "lucide-react";
import { Outlet, useLocation } from "react-router-dom";

import { Sidebar } from "@/components/layout/Sidebar";
import { useAuth } from "@/hooks/useAuth";
import { useReports } from "@/hooks/useReports";

/** Page titles, keyed by route. The top bar renders these in gradient text. */
const TITLES: Record<string, string> = {
  "/": "Reports",
  "/admin/users": "User Management",
  "/admin/access": "Report Access",
  "/admin/companies": "Companies",
};

export function AppLayout() {
  const { user } = useAuth();
  const { reports } = useReports();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  // Close the mobile drawer whenever the route changes — otherwise tapping a link on a
  // phone leaves the drawer covering the page you just navigated to.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  const report = reports.find((r) => location.pathname === `/reports/${r.key}`);
  const title = report?.name ?? TITLES[location.pathname] ?? "Quality Field";

  const initials =
    user?.displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .join("") || "?";

  const roleLabel =
    user?.role === "SYSTEM_ADMIN"
      ? "Administrator"
      : user?.role === "UNIFIBER_INTERNAL"
        ? "Unifiber Internal"
        : (user?.companyName ?? "");

  return (
    <>
      <Sidebar open={navOpen} onClose={() => setNavOpen(false)} reports={reports} />

      <div className="min-h-screen bg-slate-50 lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-4 border-b border-slate-200 bg-white px-4 lg:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={() => setNavOpen(true)}
              aria-label="Open navigation"
              className="-ml-2 rounded-lg p-2 text-slate-500 hover:bg-slate-50 hover:text-slate-800 lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
            <h1 className="gradient-text truncate text-xl font-bold lg:text-2xl">{title}</h1>
            <span className="hidden whitespace-nowrap text-xs font-medium text-slate-400 xl:inline">
              Signed in as <span className="font-semibold text-brand-purple">{roleLabel}</span>
            </span>
          </div>

          <div className="flex shrink-0 items-center gap-2 lg:gap-4">
            {/* ⚠ The mockup's search box and notification bell are deliberately NOT here
                yet. Neither has anything behind it until the reports land, and a control
                that does nothing teaches people the product is broken. They come back with
                the report whose search and snapshot notifications justify them. */}
            <div
              className="gradient-band flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white"
              title={user?.email}
            >
              {initials}
            </div>
          </div>
        </header>

        <main>
          <Outlet />
        </main>
      </div>
    </>
  );
}
